import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import { useDebounce } from "../../../hooks/use-debounce";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";
import EmptyState from "../../../components/custom/empty-state";
import TransportVehicleService, {
  TRANSPORT_VEHICLE_STATUS,
  TRANSPORT_VEHICLE_STATUS_LABEL,
  TransportVehicle,
} from "../services/transport-vehicle-service";
import TransportVehicleFormModal, {
  VehicleFormValues,
} from "../components/transport-vehicle-form-modal";
import TransportVehicleTransferModal from "../components/transport-vehicle-transfer-modal";
import { ArrowLeftRight } from "lucide-react";

/**
 * Danh mục XE VẬN CHUYỂN (xe tải trung chuyển).
 *
 * Trạng thái hiển thị có 3 mức nhưng DB chỉ lưu 2: "Đang vận chuyển" suy từ `is_in_transit` (xe còn hàng
 * trên chuyến) và được ưu tiên hiển thị vì đó là thông tin điều độ cần nhất khi nhìn danh sách.
 */
const TransportVehicleIndexPage: React.FC = () => {
  const [rows, setRows] = useState<TransportVehicle[]>([]);
  const [keySearch, setKeySearch] = useState("");
  // Gõ tới đâu gọi API tới đó thì mỗi ký tự là một request — chờ người dùng ngưng gõ 500ms rồi mới tìm.
  const debouncedSearch = useDebounce(keySearch, 500);
  const [statusFilter, setStatusFilter] = useState<number | "">("");
  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState<TransportVehicle | null>(null);
  const [deleteRow, setDeleteRow] = useState<TransportVehicle | null>(null);
  const [transferRow, setTransferRow] = useState<TransportVehicle | null>(null);

  const { fetch: listVehicles, loading } = TransportVehicleService.list();
  const { fetch: createVehicle, loading: creating } = TransportVehicleService.create();
  const { fetch: updateVehicle, loading: updating } = TransportVehicleService.update();
  const { fetch: removeVehicle, loading: removing } = TransportVehicleService.remove();

  // Trang này tự có thêm `p-4` bên ngoài nên khoảng đệm dưới gấp đôi mặc định của layout.
  const { ref: frameRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>(32);

  const listRef = useRef(listVehicles);
  useEffect(() => {
    listRef.current = listVehicles;
  }, [listVehicles]);

  const load = useCallback(async () => {
    const res = await listRef.current({
      key_search: debouncedSearch.trim() || undefined,
      status: statusFilter || undefined,
    });
    if (res && isSuccess(res)) setRows((res.data as TransportVehicle[]) || []);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () => ({
      total: rows.length,
      inTransit: rows.filter((r) => r.is_in_transit).length,
      suspended: rows.filter((r) => r.status === TRANSPORT_VEHICLE_STATUS.SUSPENDED).length,
    }),
    [rows]
  );

  const toPayload = (v: VehicleFormValues) => ({
    name: v.name.trim(),
    plate_number: v.plate_number.trim(),
    area: v.area.trim() || undefined,
    description: v.description.trim() || undefined,
    status: v.status,
    // Đội hình: gửi null tường minh để BE bỏ trống (không gửi = BE giữ nguyên).
    driver_id: v.driver_id || null,
    assistant_id: v.driver_id ? v.assistant_id || null : null,
    driver_ratio: v.assistant_id ? v.driver_ratio : 100,
  });

  const onCreate = async (v: VehicleFormValues): Promise<boolean> => {
    // Xe mới luôn Hoạt động — form tạo không hỏi trạng thái.
    const res = await createVehicle({ ...toPayload(v), status: TRANSPORT_VEHICLE_STATUS.ACTIVE });
    if (res && isSuccess(res)) {
      showToast({ message: "Đã thêm xe vận chuyển", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể thêm xe", type: "error" });
    return false;
  };

  const onEdit = async (v: VehicleFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const res = await updateVehicle(editRow.id, toPayload(v));
    if (res && isSuccess(res)) {
      showToast({ message: "Đã cập nhật xe vận chuyển", type: "success" });
      setEditRow(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật xe", type: "error" });
    return false;
  };

  const onConfirmDelete = async () => {
    if (!deleteRow) return;
    const res = await removeVehicle(deleteRow.id);
    if (res && isSuccess(res)) {
      showToast({ message: `Đã xoá xe ${deleteRow.plate_number}`, type: "success" });
      setDeleteRow(null);
      void load();
      return;
    }
    showToast({ message: res?.message ?? "Không thể xoá xe", type: "error" });
    setDeleteRow(null);
  };

  const statusBadge = (v: TransportVehicle) => {
    if (v.is_in_transit) {
      return (
        <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
          Đang vận chuyển
        </span>
      );
    }
    const isActive = v.status === TRANSPORT_VEHICLE_STATUS.ACTIVE;
    return (
      <span
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          isActive ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"
        }`}
      >
        {TRANSPORT_VEHICLE_STATUS_LABEL[v.status] || "—"}
      </span>
    );
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-green-600">Quản lý xe vận chuyển</h1>
          <p className="text-sm text-slate-500 mt-1">
            Xe tải chở hàng thẳng tới khách hoặc trung chuyển giữa điểm tập kết và nhà máy. Tổng {counts.total} xe ·{" "}
            {counts.inTransit} xe đang vận chuyển · {counts.suspended} xe tạm ngưng.
          </p>
        </div>
        <button
          onClick={() => setOpenCreate(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
          <span>+ THÊM XE</span>
        </button>
      </div>

      {/* Bộ lọc */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-lg">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Tìm kiếm</label>
          <input
            value={keySearch}
            onChange={(e) => setKeySearch(e.target.value)}
            placeholder="Tên xe hoặc biển số"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Trạng thái</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value ? Number(e.target.value) : "")}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Tất cả</option>
            <option value={TRANSPORT_VEHICLE_STATUS.ACTIVE}>Hoạt động</option>
            <option value={TRANSPORT_VEHICLE_STATUS.SUSPENDED}>Tạm ngưng</option>
          </select>
        </div>
      </div>

      {/* Bảng */}
      {/* Khung bảng cao vừa tới đáy viewport; nhiều dòng hơn thì cuộn bên trong khung, tiêu đề cột đứng yên. */}
      <div
        ref={frameRef}
        style={{ maxHeight }}
        className="mt-4 overflow-auto rounded-lg border border-gray-200 bg-white"
      >
        {loading ? (
          <div className="p-6 text-slate-500">Đang tải...</div>
        ) : rows.length === 0 ? (
          <EmptyState message="Chưa có xe vận chuyển nào" description="Không tìm thấy xe khớp bộ lọc." />
        ) : (
          // border-separate: với border-collapse, viền của ô tiêu đề sticky bị cuộn mất theo nội dung.
          <table className="min-w-full border-separate border-spacing-0 text-sm [&_tr>*:last-child]:border-r-0 [&_tbody>tr:last-child>td]:border-b-0">
            <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100">
              <tr>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-center">STT</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Tên xe</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Biển số</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Khu vực</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Đội hình</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Mô tả</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Trạng thái</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Đang chở</th>
                <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v, idx) => (
                <tr key={v.id} className="hover:bg-gray-50">
                  <td className="border-b border-r border-gray-200 px-3 py-2 text-center">{idx + 1}</td>
                  <td className="border-b border-r border-gray-200 px-3 py-2 font-medium text-slate-800">{v.name}</td>
                  <td className="border-b border-r border-gray-200 px-3 py-2">{v.plate_number}</td>
                  <td className="border-b border-r border-gray-200 px-3 py-2">{v.area || "—"}</td>
                  <td className="border-b border-r border-gray-200 px-3 py-2">
                    {v.driver_id ? (
                      <>
                        <span className="text-slate-800">
                          TX: {v.driver_name || `NV #${v.driver_id}`}{" "}
                          <span className="text-xs text-slate-400">({Number(v.driver_ratio)}%)</span>
                        </span>
                        {v.assistant_id && (
                          <span className="block text-slate-600">
                            PX: {v.assistant_name || `NV #${v.assistant_id}`}{" "}
                            <span className="text-xs text-slate-400">({Number(v.assistant_ratio)}%)</span>
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-amber-600">Chưa có tài xế — chưa dùng được</span>
                    )}
                  </td>
                  <td className="border-b border-r border-gray-200 px-3 py-2 text-slate-500">{v.description || "—"}</td>
                  <td className="border-b border-r border-gray-200 px-3 py-2 text-center">{statusBadge(v)}</td>
                  <td className="border-b border-r border-gray-200 px-3 py-2 text-slate-600">
                    {v.is_in_transit ? (
                      <>
                        {v.current_driver_name || "—"}
                        <span className="block text-xs text-slate-400">
                          {v.on_board_order_count} đơn trên xe
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="border-b border-r border-gray-200 px-3 py-2">
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditRow(v)}
                        aria-label="Sửa"
                        className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm transition hover:bg-yellow-600"
                      >
                        {/* Icon bút ✏ */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 14.25v4.5A2.25 2.25 0 0117.25 21H5.25A2.25 2.25 0 013 18.75V6.75A2.25 2.25 0 015.25 4.5h4.5"
                          />
                        </svg>
                        <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                          Sửa
                        </span>
                      </button>

                      {/* Xe hỏng / cần đổi xe → chuyển cả đội hình + đơn đang chạy sang xe trống. */}
                      {!!v.driver_id && (
                        <button
                          type="button"
                          onClick={() => setTransferRow(v)}
                          aria-label="Chuyển đội hình"
                          className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-sky-500 text-white shadow-sm transition hover:bg-sky-600"
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                          <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                            Chuyển đội hình sang xe khác
                          </span>
                        </button>
                      )}

                      {/* Chỉ xe Tạm ngưng mới xoá được — BE cũng chặn lại lần nữa. */}
                      {v.status === TRANSPORT_VEHICLE_STATUS.SUSPENDED && !v.is_in_transit && (
                        <button
                          type="button"
                          onClick={() => setDeleteRow(v)}
                          aria-label="Xoá"
                          className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-red-500 text-white shadow-sm transition hover:bg-red-600"
                        >
                          {/* Icon thùng rác 🗑 */}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="h-4 w-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                            />
                          </svg>
                          <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                            Xoá
                          </span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {openCreate && (
        <TransportVehicleFormModal
          onClose={() => setOpenCreate(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
        />
      )}
      {editRow && (
        <TransportVehicleFormModal
          onClose={() => setEditRow(null)}
          onSubmit={onEdit}
          isSubmitting={updating}
          titleText="Cập nhật xe vận chuyển"
          submitText="Cập nhật"
          showStatus
          vehicleId={editRow.id}
          initialValues={{
            name: editRow.name,
            plate_number: editRow.plate_number,
            area: editRow.area || "",
            description: editRow.description || "",
            status: editRow.status,
            driver_id: editRow.driver_id || null,
            assistant_id: editRow.assistant_id || null,
            driver_ratio: editRow.assistant_id ? Number(editRow.driver_ratio) || 50 : 50,
            driver_name: editRow.driver_name,
            assistant_name: editRow.assistant_name,
          }}
        />
      )}
      {transferRow && (
        <TransportVehicleTransferModal
          source={transferRow}
          onClose={() => setTransferRow(null)}
          onDone={() => {
            setTransferRow(null);
            void load();
          }}
        />
      )}
      {deleteRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800">Xoá xe vận chuyển</h3>
            <p className="mt-2 text-sm text-slate-600">
              Xoá xe <b>{deleteRow.name}</b> ({deleteRow.plate_number})? Lịch sử chuyến cũ vẫn được giữ để
              tra cứu.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setDeleteRow(null)}
                className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Hủy
              </button>
              <button
                onClick={onConfirmDelete}
                disabled={removing}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-70"
              >
                {removing ? "Đang xoá..." : "Xoá xe"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportVehicleIndexPage;
